import React from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";

import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";
import Box from "@material-ui/core/Box";
import TablePagination from "@material-ui/core/TablePagination";

import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RefreshIcon from "@material-ui/icons/Refresh";
import BusinessIcon from "@material-ui/icons/Business";
import StorageIcon from "@material-ui/icons/Storage";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  title: {
    fontSize: "28pt",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  statusChip: {
    fontSize: "1rem",
    fontWeight: "bold",
    minWidth: 120,
  },
  summaryCard: {
    marginBottom: theme.spacing(3),
  },
  supplierCard: {
    marginBottom: theme.spacing(2),
    textAlign: "center",
  },
  supplierIcon: {
    fontSize: 40,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  metricsGrid: {
    marginBottom: theme.spacing(3),
  },
  metricCard: {
    textAlign: "center",
    padding: theme.spacing(2),
  },
  metricIcon: {
    fontSize: 36,
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(1),
  },
  metricValue: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: theme.palette.text.primary,
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  itemsTable: {
    marginTop: theme.spacing(2),
  },
  itemRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  reasoningRow: {
    backgroundColor: theme.palette.grey[50],
    "&:hover": {
      backgroundColor: theme.palette.grey[100],
    },
  },
  reasoningCell: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(3),
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    lineHeight: 1.4,
    borderBottom: "none",
  },
  reasoningContent: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.palette.grey[300]}`,
    direction: "rtl",
    textAlign: "right",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
  },
  errorContainer: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.error.main,
  },
  sectionHeader: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    fontSize: "1.5rem",
    fontWeight: 600,
  },
});

const hebrewLabels = {
  title: "פרטי הזמנת טיוטה",
  actions: {
    back: "חזור לרשימה",
    refresh: "רענן נתונים",
  },
  status: {
    initial: "התחלתי",
    preparing: "בהכנה",
    completed: "הושלם",
    failed: "נכשל",
    cancelled: "בוטל",
  },
  summary: {
    title: "מידע כללי",
    orderId: "מזהה הזמנה",
    status: "סטטוס",
    createdAt: "נוצר בתאריך",
    createdBy: "נוצר על ידי",
    totalItems: "מספר פריטים",
    totalTires: "מספר צמיגים",
    updatedAt: "עודכן לאחרונה",
  },
  suppliers: {
    title: "חלוקה לפי ספקים",
    tires: "צמיגים",
  },
  metrics: {
    title: "מדדי ביצועים",
    lowestPrices: "מחירים נמוכים",
    rduch: "RDUCH",
    deltiFr: "Delti FR",
    deltiDe: "Delti DE",
  },
  items: {
    title: "פריטים בהזמנה",
    ean: "EAN",
    product: "מוצר",
    quantity: "כמות",
    price: "מחיר",
    supplier: "ספק",
    currentStock: "מלאי נוכחי",
    lastYearSold: "נמכר בשנה שעברה",
    rowsPerPage: "שורות בעמוד",
    of: "מתוך",
  },
  loading: "טוען נתונים...",
  error: "שגיאה בטעינת הנתונים",
  noData: "אין נתונים להצגה",
  noItems: "אין פריטים בהזמנה זו",
};

// Function to translate reasoning from English to Hebrew
const translateReasoning = (englishReason) => {
  if (!englishReason) return "";
  
  return englishReason
    .replace(/Sold (\d+) units last year/g, "נמכרו $1 יחידות בשנה שעברה")
    .replace(/Sold (\d+) units year-to-date/g, "נמכרו $1 יחידות השנה עד כה")
    .replace(/Expected to sell (\d+) units this year/g, "צפוי למכור $1 יחידות השנה")
    .replace(/Current stock: (\d+)/g, "מלאי נוכחי: $1")
    .replace(/Best price from: ([\w]+)/g, "המחיר הטוב ביותר מ: $1")
    .replace(/Unit cost: €([\d.,]+)/g, "עלות ליחידה: €$1")
    .replace(/Added item: ([\w]+)/g, "פריט נוסף: $1")
    .replace(/Manual adjustment for promotion/g, "התאמה ידנית לקידום מכירות")
    .replace(/Low stock item/g, "פריט במלאי נמוך")
    .replace(/High demand item/g, "פריט בביקוש גבוה")
    .replace(/Seasonal adjustment/g, "התאמה עונתית")
    .replace(/Test item/g, "פריט בדיקה")
    .replace(/; /g, " • ");
};

class DraftOrderDetailPage extends React.Component {
  state = {
    loading: true,
    error: null,
    order: null,
    page: 0,
    rowsPerPage: 25,
  };

  componentDidMount() {
    this.loadOrderDetails();
  }

  componentDidUpdate(prevProps) {
    const prevOrderId = prevProps.match?.params?.orderId;
    const currentOrderId = this.props.match?.params?.orderId;
    
    if (prevOrderId !== currentOrderId) {
      this.loadOrderDetails();
    }
  }

  loadOrderDetails = async () => {
    const { match, actions } = this.props;
    const orderId = match?.params?.orderId;

    if (!orderId) {
      this.setState({ error: "Missing order ID", loading: false });
      return;
    }

    this.setState({ loading: true, error: null });

    try {
      const order = await actions.getDraftOrderDetails(orderId);
      this.setState({ order, loading: false });
    } catch (error) {
      this.setState({ error: error.message || "Failed to load order", loading: false });
    }
  };

  handleBack = () => {
    this.props.history.push('/admin/draft-orders');
  };

  handleRefresh = () => {
    this.loadOrderDetails();
  };

  getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "primary";
      case "preparing":
        return "secondary";
      case "failed":
        return "default";
      case "cancelled":
        return "default";
      default:
        return "default";
    }
  };

  formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("he-IL") + " " + date.toLocaleTimeString("he-IL", { hour: '2-digit', minute: '2-digit' });
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ 
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0 
    });
  };

  renderSummaryCard = () => {
    const { classes } = this.props;
    const { order } = this.state;

    return (
      <Card className={classes.summaryCard}>
        <CardHeader
          title={hebrewLabels.summary.title}
          action={
            <Chip
              label={hebrewLabels.status[order.status] || order.status}
              color={this.getStatusColor(order.status)}
              className={classes.statusChip}
            />
          }
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>
                <strong>{hebrewLabels.summary.orderId}:</strong> {order._id}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>{hebrewLabels.summary.createdAt}:</strong> {this.formatDate(order.createdAt)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>{hebrewLabels.summary.createdBy}:</strong> {order.createdBy || "system"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" gutterBottom>
                <strong>{hebrewLabels.summary.totalItems}:</strong> {order.totalItems || 0}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>{hebrewLabels.summary.totalTires}:</strong> {order.totalTires || 0}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>{hebrewLabels.summary.updatedAt}:</strong> {this.formatDate(order.updatedAt)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  renderSupplierDistribution = () => {
    const { classes } = this.props;
    const { order } = this.state;

    if (!order.amountPerSupplier) return null;

    return (
      <Card className={classes.summaryCard}>
        <CardHeader title={hebrewLabels.suppliers.title} />
        <CardContent>
          <Grid container spacing={3}>
            {Object.entries(order.amountPerSupplier).map(([supplier, amount]) => (
              <Grid item xs={12} sm={4} key={supplier}>
                <Card className={classes.supplierCard} variant="outlined">
                  <CardContent>
                    <BusinessIcon className={classes.supplierIcon} />
                    <Typography variant="h4" color="primary">
                      {amount}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {supplier}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {hebrewLabels.suppliers.tires}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    );
  };

  renderMetrics = () => {
    const { classes } = this.props;
    const { order } = this.state;

    if (!order.metrics?.lowestPriceCounters) return null;

    const metrics = order.metrics.lowestPriceCounters;

    return (
      <Card className={classes.summaryCard}>
        <CardHeader title={hebrewLabels.metrics.title} />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Card className={classes.metricCard} variant="outlined">
                <TrendingUpIcon className={classes.metricIcon} />
                <Typography variant="h3" className={classes.metricValue}>
                  {metrics.rduch || 0}
                </Typography>
                <Typography className={classes.metricLabel}>
                  {hebrewLabels.metrics.rduch}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card className={classes.metricCard} variant="outlined">
                <TrendingUpIcon className={classes.metricIcon} />
                <Typography variant="h3" className={classes.metricValue}>
                  {metrics.deltiFr || 0}
                </Typography>
                <Typography className={classes.metricLabel}>
                  {hebrewLabels.metrics.deltiFr}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card className={classes.metricCard} variant="outlined">
                <TrendingUpIcon className={classes.metricIcon} />
                <Typography variant="h3" className={classes.metricValue}>
                  {metrics.deltiDe || 0}
                </Typography>
                <Typography className={classes.metricLabel}>
                  {hebrewLabels.metrics.deltiDe}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  renderItemsTable = () => {
    const { classes } = this.props;
    const { order, page, rowsPerPage } = this.state;

    if (!order.items || order.items.length === 0) {
      return (
        <Card className={classes.summaryCard}>
          <CardContent>
            <Typography variant="h6" align="center" color="textSecondary">
              {hebrewLabels.noItems}
            </Typography>
          </CardContent>
        </Card>
      );
    }

    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const paginatedItems = order.items.slice(startIndex, endIndex);

    return (
      <Card className={classes.summaryCard}>
        <CardHeader 
          title={`${hebrewLabels.items.title} (${order.items.length})`}
          action={
            <StorageIcon style={{ color: '#666' }} />
          }
        />
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{hebrewLabels.items.ean}</TableCell>
                  <TableCell>{hebrewLabels.items.product}</TableCell>
                  <TableCell align="center">{hebrewLabels.items.quantity}</TableCell>
                  <TableCell align="center">{hebrewLabels.items.price}</TableCell>
                  <TableCell align="center">{hebrewLabels.items.supplier}</TableCell>
                  <TableCell align="center">{hebrewLabels.items.currentStock}</TableCell>
                  <TableCell align="center">{hebrewLabels.items.lastYearSold}</TableCell>
                </TableRow>
              </TableHead>
                              <TableBody>
                {paginatedItems.flatMap((item, index) => [
                  // Main item row
                  <TableRow key={`item-${index}`} className={classes.itemRow}>
                    <TableCell>
                      <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                        {item.ean}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.product}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {item.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        €{item.unitPrice}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={item.source} 
                        size="small" 
                        color={item.source === 'RDUCH' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {item.currentStock || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {item.lastYearSold || 0}
                      </Typography>
                    </TableCell>
                  </TableRow>,
                  // Reasoning sub-row
                  item.addedReason && (
                    <TableRow key={`reason-${index}`} className={classes.reasoningRow}>
                      <TableCell colSpan={7} className={classes.reasoningCell}>
                        <div className={classes.reasoningContent}>
                          <Typography variant="caption">
                            {translateReasoning(item.addedReason)}
                          </Typography>
                        </div>
                      </TableCell>
                    </TableRow>
                                     )
                 ].filter(Boolean))}
                </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={order.items.length}
            page={page}
            onPageChange={this.handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={this.handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={hebrewLabels.items.rowsPerPage}
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} ${hebrewLabels.items.of} ${count}`
            }
          />
        </CardContent>
      </Card>
    );
  };

  render() {
    const { classes } = this.props;
    const { loading, error, order } = this.state;

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress size={60} />
          <Typography variant="h6" style={{ marginLeft: 16 }}>
            {hebrewLabels.loading}
          </Typography>
        </div>
      );
    }

    if (error) {
      return (
        <div className={classes.errorContainer}>
          <Typography variant="h6" color="error">
            {hebrewLabels.error}
          </Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={this.handleRefresh}
            style={{ marginTop: 16 }}
            startIcon={<RefreshIcon />}
          >
            {hebrewLabels.actions.refresh}
          </Button>
        </div>
      );
    }

    if (!order) {
      return (
        <div className={classes.errorContainer}>
          <Typography variant="h6">
            {hebrewLabels.noData}
          </Typography>
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h3" className={classes.title}>
            {hebrewLabels.title}
          </Typography>
          <div className={classes.headerActions}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={this.handleBack}
              className={classes.backButton}
            >
              {hebrewLabels.actions.back}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={this.handleRefresh}
            >
              {hebrewLabels.actions.refresh}
            </Button>
          </div>
        </div>

        {this.renderSummaryCard()}
        {this.renderSupplierDistribution()}
        {this.renderMetrics()}
        {this.renderItemsTable()}
      </div>
    );
  }
}

DraftOrderDetailPage.propTypes = {
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withRouter(withStyles(styles)(DraftOrderDetailPage)); 